import React, { Component } from "react";
import { Alert } from 'react-bootstrap'
import StateList from "../components/StateList";
import { Rifm } from 'rifm';
import { Form } from 'react-bootstrap'
import { Checkbox } from 'react-input-checkbox';
import { ValidatableFormGroup } from "../components/ValidatableFormGroup"
import { IAddress, IAddressChangeResult, IUSPSResult } from "../classes/Domain";
import { validationUtils } from "../validation/Validation";
import Utilities from "../classes/Utilities";
import USPSClient from "../classes/USPSClient";
import { GARAGING_COUNTRY, GARAGING_COUNTRY_OPTIONS } from "../config/StateQuoteConfig";

type Style = {
    display: 'none' | '';
}

type AddressLabels = {
    street: string,
    apartment: string,
    city: string,
    stateCode: string,
    zipCode: string,
    country?: string
}

type AddressPlaceholders = {
    street: string,
    apartment: string,
    city: string,
    stateCode: string,
    zipCode: string,
    country?: string
}

type FieldsVisibility = {
    apartment: boolean;
}

type AddressProps = {
    value: IAddress | null,
    onChange(result: IAddressChangeResult) : void,
    label?: string,
    zipCodeDisabled?: boolean,
    noAddressVerification?:boolean,
    errors: any,
    labels?: AddressLabels,
    fieldsVisibility?: FieldsVisibility,
    placeholders?: AddressPlaceholders,
    isStateEditable?: boolean,
    style?: Style,
    isAllFieldsEditable?: boolean,
    /** Qualitas garaging: show USA | Mexico country select (Q7). */
    showCountry?: boolean;
}

type RState = {
    //address: IAddress,
    showAlertZip: boolean,
    showAlertAddress: boolean,
    confirmSkipVerification: boolean,
    isHide: boolean;
}

export class Address extends Component<AddressProps, RState> {
    private uspsVerifySeq = 0;

    constructor(props: AddressProps) {
        super(props);

        this.onSkipAddressVerification = this.onSkipAddressVerification.bind(this);
        this.needAddressVerification = this.needAddressVerification.bind(this);
                
        this.state = {
            isHide: true,
            showAlertZip: false,
            showAlertAddress: false,
            confirmSkipVerification: false
        };
    }

    static addressesAreEqual(address1: IAddress, address2: IAddress): boolean {
        return address1.street === address2.street
            && address1.apartment === address2.apartment
            && address1.city === address2.city
            && address1.stateCode === address2.stateCode
            && address1.zipCode === address2.zipCode
            && (address1.country || "") === (address2.country || "")
            ;
    }

    private needAddressVerification() {
        if (this.props.value?.country && this.props.value.country !== GARAGING_COUNTRY.USA) {
            return false;
        }
        return !this.props.hasOwnProperty("noAddressVerification")
            || !this.props.noAddressVerification;
    }

    private verifyZip(address: IAddress) {
        const seq = ++this.uspsVerifySeq;
        this.props.onChange({
            address,
            isFull: Utilities.isAddressFilled(address),
            isValid: false,
            isVerifying: true
        });

        var p =  new USPSClient().getAreaByZip(address.zipCode)
            .then((result: IUSPSResult) => {
                if (seq !== this.uspsVerifySeq) {
                    return result;
                }

                console.log("zip is valid");

                const city = result.city.toString();
                const stateCode = result.state.toString();
                const zipCode = result.zip.toString();

                // Skip onChange when USPS result already matches — avoids remount/update loops
                // when Address mounts with zip prefilled but street still empty.
                if (
                    (address.city || "").toUpperCase() === city.toUpperCase()
                    && (address.stateCode || "").toUpperCase() === stateCode.toUpperCase()
                    && (address.zipCode || "") === zipCode
                ) {
                    this.props.onChange({
                        address,
                        isFull: Utilities.isAddressFilled(address),
                        isVerifying: false
                    });
                    return result;
                }

                const uspsAddress = {
                    ...address,
                    city,
                    stateCode,
                    zipCode
                };

                this.props.onChange({
                    address: uspsAddress,
                    isFull: Utilities.isAddressFilled(uspsAddress),
                    isVerifying: false
                });
                return result;
            },
            (error) => {
                if (seq === this.uspsVerifySeq) {
                    console.log("zip is invalid");
                    this.props.onChange({
                        address,
                        isFull: Utilities.isAddressFilled(address),
                        isVerifying: false
                    });
                }
                //this.setState({ ...this.state, showAlertZip: true });
                throw error;
            });

        return p;
    }

    private verifyFullAddress(address: IAddress) {
        const seq = ++this.uspsVerifySeq;
        this.props.onChange({
            address,
            isFull: Utilities.isAddressFilled(address),
            isValid: false,
            isVerifying: true
        });

        var p = new USPSClient().verifyAddress(address);
        p.then(() => {
            if (seq !== this.uspsVerifySeq) {
                return;
            }
            console.log("address is valid");
            this.props.onChange({ address: address, isFull: Utilities.isAddressFilled(address), isValid: true, isVerifying: false });
        },
        (error) => {
            if (seq !== this.uspsVerifySeq) {
                return;
            }
            console.log("address is invalid");
            this.setState({ ...this.state, showAlertAddress: true });
            this.props.onChange({
                address: address,
                isFull: Utilities.isAddressFilled(address),
                isValid: this.needAddressVerification() ? false : true,
                isVerifying: false
            });
            //throw error;
        });

        return p;
    }

    componentDidMount() {
        if (null != this.props.value && Utilities.isFullZip(this.props.value.zipCode) && !Utilities.isAddressFilled(this.props.value)) {
            this.verifyZip(this.props.value).catch(() => console.log("Invalid zip"));
        }
        else if (null != this.props.value && Utilities.isAddressFilled(this.props.value) && this.needAddressVerification()) {
            this.verifyFullAddress(this.props.value);
        }
    }

    componentDidUpdate(prevProps: AddressProps) {
        const address = this.props.value;
        const prev = prevProps.value;
        if (!address || !Utilities.isFullZip(address.zipCode)) {
            return;
        }
        // Vehicle cascade can rebuild garaging with zip/state but empty city; re-fill from USPS.
        const cityMissing = !address.city;
        const zipChanged = !prev || prev.zipCode !== address.zipCode;
        if (cityMissing || (zipChanged && !Utilities.isAddressFilled(address))) {
            if (!prev || prev.city !== address.city || prev.zipCode !== address.zipCode || zipChanged) {
                this.verifyZip(address).catch(() => console.log("Invalid zip"));
            }
        }
    }

    onAddressChange(value: string, prop: string, verifyAddress: boolean = false) {
        const updatedAddress = { ...this.props.value } as IAddress;
        updatedAddress[prop] = value;

        const willVerifyUsps = verifyAddress
            || ("zipCode" === prop && Utilities.isFullZip(value) && this.needAddressVerification());

        this.props.onChange({
            address: updatedAddress,
            isFull: Utilities.isAddressFilled(updatedAddress),
            isValid: !willVerifyUsps,
            isVerifying: willVerifyUsps
        });

        this.setState({ showAlertZip: false, showAlertAddress: false, confirmSkipVerification: false },
            () => {
                if ("zipCode" === prop && Utilities.isFullZip(value)) {
                    this.verifyZip(updatedAddress)
                        .then((result: IUSPSResult) => {
                            updatedAddress.city = result.city;
                            updatedAddress.stateCode = result.state;
                            updatedAddress.zipCode = result.zip;
                            if (Utilities.isAddressFilled(updatedAddress)) {
                                if (verifyAddress)
                                    this.verifyFullAddress(updatedAddress);
                                else
                                    this.props.onChange({ address: updatedAddress, isFull: true, isValid: true, isVerifying: false });
                            }
                        })
                        .catch(() => {
                            console.log("onAddressChange - zip - catch")
                            this.props.onChange({
                                address: updatedAddress,
                                isFull: Utilities.isAddressFilled(updatedAddress),
                                isVerifying: false
                            });
                        });
                }
                else if (verifyAddress && Utilities.isAddressFilled(updatedAddress)) {
                    this.verifyFullAddress(updatedAddress)
                        .then((result: IUSPSResult) => {
                            console.log("onAddressChange - valid address")
                            this.props.onChange({ address: updatedAddress, isFull: true, isValid: true, isVerifying: false });
                        },
                        () => {
                            console.log("onAddressChange - INvalid address")
                            this.props.onChange({ address: updatedAddress, isFull: true, isValid: false, isVerifying: false });
                        });
                }
                //else {
                //    this.props.onChange({ address: updatedAddress, isFull: false });
                //}
            });
    }

    onSkipAddressVerification() {
        const skip = !this.state.confirmSkipVerification;

        this.setState({ confirmSkipVerification: skip },
            () => {
                const address = { ...this.props.value } as IAddress;
                this.props.onChange({ address, isFull: Utilities.isAddressFilled(address), isValid: skip, isVerifying: false });
            });
    }

    render() {
        return (
            <div className="address" style={this.props.style}>

                <Alert show={this.state.showAlertZip} variant="danger">
                    Zip Code {this.props.value?.zipCode} is invalid
                </Alert>

                <Alert show={this.state.showAlertAddress} variant="danger">
                    <p>Address {Utilities.formatAddress(this.props.value)} Not Found</p>
                    <p className="skip-verification">
                        <Checkbox theme="bootstrap-checkbox"
                            children={[]}
                            value={this.state.confirmSkipVerification}
                            onChange={() => this.onSkipAddressVerification()}></Checkbox>
                        <label className="confirm-text">I confirm the address provided is correct and wish to bypass the USPS address verification</label>
                    </p>
                </Alert>

                {
                    !!this.props.label
                    ? <Form.Label>{this.props.label}</Form.Label>
                    : null
                }

                {
                    this.props.showCountry
                        ? <Form.Group className="country">
                            <Form.Label>{this.props.labels?.country ?? "Country"}</Form.Label>
                            <Form.Control
                                as="select"
                                value={Utilities.getStringValueSafe(this.props.value, "country") || GARAGING_COUNTRY.USA}
                                onChange={(e) => this.onAddressChange(e.target.value, "country")}
                                isInvalid={null != this.props.errors && validationUtils.hasValidationErrorFor(this.props.errors, "country")}
                            >
                                {GARAGING_COUNTRY_OPTIONS.map(o => (
                                    <option key={o.value} value={o.value}>{o.label}</option>
                                ))}
                            </Form.Control>
                            {validationUtils.hasValidationErrorFor(this.props.errors, "country")
                                ? <Form.Control.Feedback type="invalid">
                                    {validationUtils.getValidationErrorFor(this.props.errors, "country")}
                                </Form.Control.Feedback>
                                : null
                            }
                        </Form.Group>
                        : null
                }

                {
                    (this.props.isAllFieldsEditable ?? true)
                        ?
                        <div className="address-fields">
                            <Rifm
                                accept={/\d/g}
                                format={Utilities.formatInteger}
                                value={Utilities.getStringValueSafe(this.props.value, "zipCode") || ""}
                                onChange={(v) => { this.onAddressChange(v, "zipCode", this.needAddressVerification()) }}>
                                {({ value, onChange }) => (
                                    <ValidatableFormGroup
                                        label={this.props.labels?.zipCode ?? "Zip"}
                                        placeholder={this.props.placeholders?.zipCode ?? "Zip"}
                                        className="zip"
                                        value={value}
                                        validated={null !== this.props.errors}
                                        disabled={this.props.zipCodeDisabled}
                                        maxLength={5}
                                        validationError={validationUtils.getValidationErrorFor(this.props.errors, "zipCode")}
                                        onChange={(e) => { onChange(e) }}
                                        pattern="[0-9]*"
                                        inputmode="numeric" />
                                )}
                            </Rifm>

                            <ValidatableFormGroup
                                label={this.props.labels?.city ?? "City"}
                                placeholder={this.props.placeholders?.city ?? "City"}
                                className="city"
                                value={Utilities.getStringValueSafe(this.props.value, "city") || ""}
                                validated={null !== this.props.errors}
                                validationError={validationUtils.hasValidationErrorFor(this.props.errors, "city")}
                                onChange={(e) => this.onAddressChange(e.target.value, "city")}
                                onBlur={(e) => this.onAddressChange(e.target.value, "city", this.needAddressVerification())} />

                            {
                                (this.props.isStateEditable ?? false)
                                    ?
                                    <StateList
                                        label={this.props.labels?.stateCode ?? "State"}
                                        className="state"
                                        placeholder={this.props.placeholders?.stateCode ?? "State"}
                                        value={Utilities.getStringValueSafe(this.props.value, "stateCode") || ""}
                                        validated={null !== this.props.errors}
                                        validationError={validationUtils.getValidationErrorFor(this.props.errors, "stateCode")}
                                        onChange={(state) => { this.onAddressChange(state?.value ?? "", "stateCode", this.needAddressVerification()) }} />
                                    :
                                    <Form.Group className="state">
                                        <Form.Label>{this.props.labels?.stateCode ?? "St."}</Form.Label>
                                        <Form.Control disabled
                                            value={Utilities.getStringValueSafe(this.props.value, "stateCode") || ""}
                                            placeholder={this.props.placeholders?.stateCode ?? "State"} />
                                        {validationUtils.hasValidationErrorFor(this.props.errors, "stateCode")
                                            ? <Form.Control.Feedback type="invalid">
                                                {validationUtils.getValidationErrorFor(this.props.errors, "stateCode")}
                                            </Form.Control.Feedback>
                                            : null
                                        }
                                    </Form.Group>

                            }

                            <ValidatableFormGroup
                                label={this.props.labels?.street ?? "Street Address"}
                                placeholder={this.props.placeholders?.street ?? "Street Address"}
                                className={"street" + (this.props.fieldsVisibility && this.props.fieldsVisibility.hasOwnProperty("apartment") && !this.props.fieldsVisibility.apartment ? " no-apartment" : "")}
                                value={Utilities.getStringValueSafe(this.props.value, "street") || ""}
                                validated={null !== this.props.errors}
                                validationError={validationUtils.getValidationErrorFor(this.props.errors, "street")}
                                onChange={(e) => { console.log("street change"); console.log(e); this.onAddressChange(e.target.value, "street"); }}
                                onBlur={(e) => this.onAddressChange(e.target.value, "street", this.needAddressVerification())} />

                            {
                                (this.props.fieldsVisibility?.apartment ?? true)
                                    ?
                                    <ValidatableFormGroup
                                        label={this.props.labels?.apartment ?? "Apt."}
                                        placeholder={this.props.placeholders?.apartment ?? "Apt."}
                                        className="apt"
                                        value={Utilities.getStringValueSafe(this.props.value, "apartment") || ""}
                                        validated={null !== this.props.errors}
                                        validationError={validationUtils.getValidationErrorFor(this.props.errors, "apartment")}
                                        onChange={(e) => this.onAddressChange(e.target.value, "apartment")}
                                        onBlur={(e) => this.onAddressChange(e.target.value, "apartment", this.needAddressVerification())} />
                                    : null
                            }
                        </div>
                        :
                        <div className="address-fields">
                            <Form.Group className="zip">
                                <Form.Label>{this.props.labels?.zipCode ?? "Zip"}</Form.Label>
                                <Form.Control disabled
                                    value={Utilities.getStringValueSafe(this.props.value, "zipCode") || ""}
                                    placeholder={this.props.placeholders?.zipCode ?? "Zip"} />
                                {validationUtils.getValidationErrorFor(this.props.errors, "zipCode")
                                    ? <Form.Control.Feedback type="invalid">
                                        {validationUtils.getValidationErrorFor(this.props.errors, "zipCode")}
                                    </Form.Control.Feedback>
                                    : null
                                }
                            </Form.Group>

                            <Form.Group className="city">
                                <Form.Label>{this.props.labels?.city ?? "City"}</Form.Label>
                                <Form.Control disabled
                                    value={Utilities.getStringValueSafe(this.props.value, "city") || ""}
                                    placeholder={this.props.placeholders?.city ?? "City"} />
                                {validationUtils.hasValidationErrorFor(this.props.errors, "city")
                                    ? <Form.Control.Feedback type="invalid">
                                        {validationUtils.getValidationErrorFor(this.props.errors, "city")}
                                    </Form.Control.Feedback>
                                    : null
                                }
                            </Form.Group>

                            <Form.Group className="state">
                                <Form.Label>{this.props.labels?.stateCode ?? "State"}</Form.Label>
                                <Form.Control disabled
                                    value={Utilities.getStringValueSafe(this.props.value, "stateCode") || ""}
                                    placeholder={this.props.placeholders?.stateCode ?? "State"} />
                                {validationUtils.hasValidationErrorFor(this.props.errors, "stateCode")
                                    ? <Form.Control.Feedback type="invalid">
                                        {validationUtils.getValidationErrorFor(this.props.errors, "stateCode")}
                                    </Form.Control.Feedback>
                                    : null
                                }
                            </Form.Group>

                            <Form.Group className={"street" + (this.props.fieldsVisibility && this.props.fieldsVisibility.hasOwnProperty("apartment") && !this.props.fieldsVisibility.apartment ? " no-apartment" : "")}>
                                <Form.Label>{this.props.labels?.street ?? "Street Address"}</Form.Label>
                                <Form.Control disabled
                                    value={Utilities.getStringValueSafe(this.props.value, "street") || ""}
                                    placeholder={this.props.placeholders?.street ?? "Street Address"} />
                                {validationUtils.getValidationErrorFor(this.props.errors, "street")
                                    ? <Form.Control.Feedback type="invalid">
                                        {validationUtils.getValidationErrorFor(this.props.errors, "street")}
                                    </Form.Control.Feedback>
                                    : null
                                }
                            </Form.Group>

                            {
                                (this.props.fieldsVisibility?.apartment ?? true)
                                    ?
                                    <Form.Group className="apt">
                                        <Form.Label>{this.props.labels?.apartment ?? "Apt #"}</Form.Label>
                                        <Form.Control disabled
                                            value={Utilities.getStringValueSafe(this.props.value, "apartment") || ""}
                                            placeholder={this.props.placeholders?.apartment ?? "Apt #"} />
                                        {validationUtils.getValidationErrorFor(this.props.errors, "apartment")
                                            ? <Form.Control.Feedback type="invalid">
                                                {validationUtils.getValidationErrorFor(this.props.errors, "apartment")}
                                            </Form.Control.Feedback>
                                            : null
                                        }
                                    </Form.Group>
                                    : null
                            }
                        </div>
                }
            </div>
        );
    }
}
export default Address;
